import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Star, Loader2, UserPlus, Sparkles, MessageCircle, Clock, Check, Copy,
  CreditCard, PlayCircle, ExternalLink, ChevronRight, Lock,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const copyText = async (text, ok) => {
  try { await navigator.clipboard.writeText(text); toast.success(ok || "Copiado"); }
  catch { toast.error("No se pudo copiar"); }
};

const Shortcuts = ({ data, testPrefix }) => (
  <div className="flex flex-wrap gap-3 justify-center">
    <Link
      to="/registro"
      data-testid={`${testPrefix}-register-btn`}
      className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#d4af37] text-black font-bold text-xs uppercase tracking-[0.2em] hover:bg-[#e6c35a] transition-colors"
    >
      <UserPlus className="w-4 h-4" /> Registrarme gratis
    </Link>
    <Link
      to="/servicios"
      data-testid={`${testPrefix}-services-btn`}
      className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-[#d4af37]/50 text-[#d4af37] text-xs uppercase tracking-[0.2em] hover:bg-[#d4af37]/10 transition-colors"
    >
      <Sparkles className="w-4 h-4" /> Ver servicios
    </Link>
    <a
      href={data.whatsapp_link}
      target="_blank"
      rel="noreferrer"
      data-testid={`${testPrefix}-whatsapp-btn`}
      className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-emerald-500/15 border border-emerald-400/40 text-emerald-200 text-xs uppercase tracking-[0.2em] hover:bg-emerald-500/25 transition-colors"
    >
      <MessageCircle className="w-4 h-4" /> Hablar por WhatsApp
    </a>
  </div>
);

const VideoCard = ({ video, index }) => (
  <motion.article
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.6, delay: index * 0.1 }}
    data-testid={`welcome-video-${video.id}`}
    className="rounded-3xl border border-[#d4af37]/25 bg-gradient-to-b from-white/[0.04] to-transparent overflow-hidden"
  >
    <div className="relative bg-black aspect-video">
      <video
        data-testid={`welcome-video-player-${video.id}`}
        className="w-full h-full"
        controls
        playsInline
        preload="metadata"
        src={video.url}
      >
        Tu navegador no soporta video. <a href={video.url}>Abrir video</a>
      </video>
    </div>
    <div className="p-6 lg:p-8">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#d4af37] mb-3">
        <PlayCircle className="w-3.5 h-3.5" /> Video {index + 1}
        {video.duration && (<><span className="text-neutral-600">·</span><Clock className="w-3 h-3" /> {video.duration}</>)}
      </div>
      <h3 className="font-serif text-2xl lg:text-3xl text-white leading-tight mb-3">{video.title}</h3>
      <p className="text-sm text-neutral-300 leading-relaxed mb-5">{video.description}</p>
      {video.highlights?.length > 0 && (
        <ul className="space-y-2 mb-5">
          {video.highlights.map((h, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-neutral-200">
              <Check className="w-4 h-4 text-[#d4af37] shrink-0 mt-0.5" /> <span>{h}</span>
            </li>
          ))}
        </ul>
      )}
      <a
        href={video.url}
        target="_blank"
        rel="noreferrer"
        data-testid={`welcome-video-open-${video.id}`}
        className="inline-flex items-center gap-1.5 text-xs text-neutral-400 hover:text-[#d4af37]"
      >
        <ExternalLink className="w-3.5 h-3.5" /> Abrir video en pantalla completa
      </a>
    </div>
  </motion.article>
);

const PaymentMethodCard = ({ m }) => (
  <div data-testid={`welcome-payment-${m.id}`} className="rounded-2xl border border-white/10 bg-black/40 p-5 flex flex-col">
    <div className="flex items-start justify-between gap-3 mb-3">
      <h4 className="font-serif text-xl text-white leading-tight">{m.label}</h4>
      <span className="shrink-0 text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border border-[#d4af37]/40 text-[#d4af37]">
        {m.currency}{m.country && m.country !== "ALL" ? ` · ${m.country === "MX" ? "México" : "Internacional"}` : ""}
      </span>
    </div>
    <p className="text-sm text-neutral-300 whitespace-pre-wrap leading-relaxed flex-1">{m.instructions}</p>
    <button
      data-testid={`welcome-payment-copy-${m.id}`}
      onClick={() => copyText(m.instructions, "Datos de pago copiados")}
      className="mt-4 self-start inline-flex items-center gap-1.5 text-xs text-[#d4af37] hover:text-white"
    >
      <Copy className="w-3.5 h-3.5" /> Copiar datos
    </button>
  </div>
);

export default function WelcomePage() {
  const { user, loading: authLoading } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    document.title = "Bienvenido · CrisAngel Astral";
    if (authLoading) return;
    const token = localStorage.getItem("ca_token");
    axios.get(`${API}/welcome/public`, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((r) => setData(r.data)).catch(() => setError(true));
  }, [authLoading, user]);

  if (error) {
    return <div className="min-h-screen bg-[#050505] flex items-center justify-center text-neutral-400">No se pudo cargar la página. Intenta de nuevo.</div>;
  }
  if (!data) {
    return <div className="min-h-screen bg-[#050505] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#d4af37]" /></div>;
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="welcome-page">
      {/* HERO */}
      <section className="relative px-6 pt-16 pb-12 lg:pt-24 lg:pb-16 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse at top, rgba(212,175,55,0.14), transparent 60%)" }} />
        <div className="relative max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 text-[#d4af37] mb-5">
            <div className="w-10 h-px bg-[#d4af37]/40" />
            <Star className="w-4 h-4 fill-[#d4af37]" />
            <span className="uppercase tracking-[0.35em] text-[10px]">CrisAngel Astral</span>
            <Star className="w-4 h-4 fill-[#d4af37]" />
            <div className="w-10 h-px bg-[#d4af37]/40" />
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight mb-5" data-testid="welcome-headline">{data.headline}</h1>
          <p className="text-base md:text-lg text-neutral-300 max-w-2xl mx-auto mb-8" data-testid="welcome-intro">{data.intro}</p>
          <Shortcuts data={data} testPrefix="welcome-hero" />
        </div>
      </section>

      {/* VIDEOS */}
      <section className="px-6 pb-16 lg:pb-24">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8">
          {data.videos.map((v, i) => <VideoCard key={v.id} video={v} index={i} />)}
        </div>
      </section>

      {/* STEPS */}
      <section className="px-6 pb-16 lg:pb-24" data-testid="welcome-steps">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <span className="uppercase tracking-[0.3em] text-xs text-[#d4af37]">Cómo funciona</span>
            <h2 className="font-serif text-base md:text-lg text-neutral-300 mt-2">Cuatro pasos sencillos para comenzar</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {data.steps.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                data-testid={`welcome-step-${i + 1}`}
                className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 relative"
              >
                <div className="w-10 h-10 rounded-full bg-[#d4af37] text-black font-bold flex items-center justify-center font-serif text-lg mb-4">{i + 1}</div>
                <h3 className="font-serif text-xl text-white mb-2">{s.title}</h3>
                <p className="text-sm text-neutral-400 leading-relaxed">{s.text}</p>
                {i < data.steps.length - 1 && <ChevronRight className="hidden lg:block absolute -right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#d4af37]/40" />}
              </motion.div>
            ))}
          </div>
          <div className="mt-8 flex justify-center">
            <Link to="/registro" data-testid="welcome-steps-register-btn" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#d4af37] text-black font-bold text-xs uppercase tracking-[0.2em] hover:bg-[#e6c35a]">
              <UserPlus className="w-4 h-4" /> Empezar · Paso 1
            </Link>
          </div>
        </div>
      </section>

      {/* PAYMENT METHODS (members only) */}
      <section className="px-6 pb-16 lg:pb-24" data-testid="welcome-payments">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <span className="inline-flex items-center gap-2 uppercase tracking-[0.3em] text-xs text-[#d4af37]"><CreditCard className="w-4 h-4" /> Métodos de pago</span>
            <h2 className="font-serif text-base md:text-lg text-neutral-300 mt-2">
              {data.logged_in ? "Elige el que más te convenga y envía tu comprobante por WhatsApp" : "Disponibles al crear tu cuenta gratis"}
            </h2>
          </div>
          {data.logged_in ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {data.payment_methods.map((m) => <PaymentMethodCard key={m.id} m={m} />)}
            </div>
          ) : (
            <div data-testid="welcome-payments-locked" className="max-w-2xl mx-auto rounded-3xl border border-[#d4af37]/30 bg-black/40 p-8 text-center">
              <div className="w-12 h-12 rounded-full bg-[#d4af37]/15 border border-[#d4af37]/40 flex items-center justify-center mx-auto mb-4">
                <Lock className="w-5 h-5 text-[#d4af37]" />
              </div>
              <h3 className="font-serif text-2xl text-white mb-2">Regístrate para ver los métodos de pago</h3>
              <p className="text-sm text-neutral-300 mb-6">Transferencia, PayPal, remesas y más. Al crear tu cuenta verás todos los servicios con precios y los datos para pagar.</p>
              <div className="flex flex-wrap gap-3 justify-center">
                <Link to="/registro" data-testid="welcome-payments-register-btn" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#d4af37] text-black font-bold text-xs uppercase tracking-[0.2em] hover:bg-[#e6c35a]">
                  <UserPlus className="w-4 h-4" /> Registrarme gratis
                </Link>
                <Link to="/ingresar?next=/bienvenida" data-testid="welcome-payments-login-btn" className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-[#d4af37]/50 text-[#d4af37] text-xs uppercase tracking-[0.2em] hover:bg-[#d4af37]/10">
                  Ya tengo cuenta
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="px-6 pb-20">
        <div className="max-w-3xl mx-auto rounded-3xl border border-[#d4af37]/30 bg-gradient-to-b from-[#d4af37]/10 to-transparent p-8 lg:p-12 text-center">
          <h2 className="font-serif text-3xl lg:text-4xl text-white mb-3">Tu destino te espera</h2>
          <p className="text-sm text-neutral-300 mb-7">Regístrate gratis hoy o escríbeme por WhatsApp al {data.whatsapp}.</p>
          <Shortcuts data={data} testPrefix="welcome-footer" />
        </div>
      </section>
    </div>
  );
}
